// src/components/admin/ProblemManagerModal.js - KOMPLETNA, POŁĄCZONA I POPRAWIONA WERSJA

import React, { useState, useEffect } from 'react';
import { RenderMarkdownWithLatex } from '../../utils/utils';

const apiUrl = process.env.REACT_APP_API_SOURCE;
const token = localStorage.getItem("token");

// ====================================================================
//  KOMPONENT #1: RelationsManager (zawarty w tym pliku)
// ====================================================================
const RelationsManager = ({ 
  problemId, 
  title, 
  initialItems, 
  searchEndpoint, 
  linkEndpoint,
  deleteEndpointPrefix,
  itemKey, 
  renderItem
}) => {
  const [items, setItems] = useState(initialItems);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => { setItems(initialItems); }, [initialItems]);

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsLoading(true);
    const handler = setTimeout(() => {
      fetch(`${apiUrl}${searchEndpoint}?q=${searchQuery}`, {
        headers: { "Content-Type": "application/json", Authorization: `Token ${token}` }
      })
      .then(res => {
        if (!res.ok) throw new Error("Błąd podczas wyszukiwania");
        return res.json();
      })
      .then(data => setSearchResults(data))
      .catch(err => console.error(err))
      .finally(() => setIsLoading(false));
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery, searchEndpoint]);

  const handleAddItem = (itemToAdd) => {
    if (items.find(i => i[itemKey] === itemToAdd[itemKey])) {
        alert("Ten element jest już powiązany.");
        setSearchQuery('');
        setSearchResults([]);
        return;
    }
    
    // Budujemy payload - dane do wysłania do API
    let payload = {
        problem: problemId,
        [itemKey]: itemToAdd[itemKey]
    };
    
    // POPRAWKA: Jeśli dodajemy powiązanie zadania maturalnego, dołączamy punkty
    if (linkEndpoint === 'matura-problem-matches') {
        payload.points_assigned = itemToAdd.matura_problem_points || 0;
    }
    
    fetch(`${apiUrl}${linkEndpoint}/`, {
        method: 'POST',
        headers: { "Content-Type": "application/json", Authorization: `Token ${token}` },
        body: JSON.stringify(payload)
    })
    .then(res => {
        if (!res.ok) {
            // Próba odczytania błędu z API
            res.json().then(err => alert(`Błąd podczas dodawania powiązania: ${err.detail || 'Nieznany błąd'}`)).catch(() => alert("Błąd podczas dodawania powiązania."));
            throw new Error("Błąd dodawania");
        }
        return res.json();
    })
    .then(newItem => {
        const matchIdKey = `${deleteEndpointPrefix.replace(/-/g, '_')}_id`;
        const newDisplayItem = { ...itemToAdd, match_id: newItem[matchIdKey] || newItem.id };
        setItems(prevItems => [...prevItems, newDisplayItem]);
        setSearchQuery('');
        setSearchResults([]);
    })
    .catch(err => console.error(err.message));
  };

  const handleDeleteItem = (matchId) => {
    if (!window.confirm("Czy na pewno chcesz usunąć to powiązanie?")) return;
    fetch(`${apiUrl}${deleteEndpointPrefix}/${matchId}/`, {
        method: 'DELETE',
        headers: { Authorization: `Token ${token}` }
    })
    .then(res => {
        if (res.ok || res.status === 204) {
            setItems(prevItems => prevItems.filter(i => i.match_id !== matchId));
        } else {
            alert("Nie udało się usunąć powiązania.");
        }
    });
  };

  return (
    <div className="border-t-2 border-gray-200 pt-6 mt-6">
      <h3 className="text-xl font-semibold mb-3 text-gray-800">{title}</h3>
      <div className="space-y-2 mb-4">
        {items.map(item => (
          <div key={item.match_id} className="flex justify-between items-center p-3 border rounded-md bg-gray-50 text-sm">
            <div className="pr-4 w-full">{renderItem(item)}</div>
            <button onClick={() => handleDeleteItem(item.match_id)} className="text-red-500 hover:text-red-700 font-bold ml-4 flex-shrink-0 text-2xl">&times;</button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-gray-500">Brak powiązań.</p>}
      </div>
      <div className="relative">
        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Szukaj, aby dodać..." className="border p-2 rounded w-full shadow-sm" />
        {isLoading && <p className="text-xs text-gray-500 mt-1">Szukam...</p>}
        {searchResults.length > 0 && (
          <ul className="absolute z-10 w-full bg-white border rounded mt-1 max-h-60 overflow-y-auto shadow-lg">
            {searchResults.map(result => (<li key={result[itemKey]} onClick={() => handleAddItem(result)} className="p-3 hover:bg-gray-100 cursor-pointer text-sm">{renderItem(result)}</li>))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ====================================================================
//  KOMPONENT #2: ProblemManagerModal (główny, eksportowany)
// ====================================================================
const renderFormula = (item) => (
    <div>
      <p className="font-medium"><RenderMarkdownWithLatex text={item.matura_formula_name || ''} /></p>
      {item.matura_formula_text && <div className="text-xs text-gray-600"><RenderMarkdownWithLatex text={item.matura_formula_text} /></div>}
    </div>
);
  
const renderProblem = (item) => (
    <div>
      <div className="font-medium"><RenderMarkdownWithLatex text={item.matura_problem_question || ''} /></div>
      {item.matura_problem_source && <p className="text-xs text-gray-600">({item.matura_problem_source})</p>}
    </div>
);

const ProblemManagerModal = ({ problem, onClose, onDataChange }) => {
  const [elo, setElo] = useState(problem.problem_elo);
  const [detailedProblem, setDetailedProblem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    fetch(`${apiUrl}problems/${problem.problem_id}/`, {
      headers: { "Content-Type": "application/json", Authorization: `Token ${token}` }
    })
    .then(res => res.json())
    .then(data => {
        setDetailedProblem(data);
        setElo(data.problem_elo);
    })
    .finally(() => setIsLoading(false));
  }, [problem.problem_id]);

  const handleSaveElo = () => {
    fetch(`${apiUrl}problems/${problem.problem_id}/`, {
        method: 'PATCH',
        headers: { "Content-Type": "application/json", Authorization: `Token ${token}` },
        body: JSON.stringify({ problem_elo: elo })
    })
    .then(res => {
        if (res.ok) {
            alert("Zapisano ELO!");
            onDataChange(); 
        } else {
            alert("Błąd podczas zapisywania ELO.");
        }
    });
  };

  if (!problem) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-6 border-b-2 pb-4">
          <h2 className="text-3xl font-bold text-gray-800">Zarządzaj Zadaniem (ID: {problem.problem_id})</h2>
          <button onClick={onClose} className="text-4xl text-gray-500 hover:text-gray-800">&times;</button>
        </div>
        
        {isLoading && <p>Ładowanie szczegółów zadania...</p>}
        
        {!isLoading && detailedProblem && (
          <div className="flex-grow overflow-y-auto pr-4">
            <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
                <h3 className="text-xl font-semibold mb-2 text-gray-700">Podgląd zadania</h3>
                <div className="text-base">
                    <RenderMarkdownWithLatex text={detailedProblem.translations[0]?.question || 'Brak treści pytania.'} />
                </div>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <label htmlFor="problem-elo" className="text-lg font-semibold">ELO zadania:</label>
              <input id="problem-elo" type="number" value={elo} onChange={e => setElo(parseInt(e.target.value))} className="border p-2 rounded w-32 shadow-sm text-lg"/>
              <button onClick={handleSaveElo} className="bg-blue-600 text-white px-5 py-2 rounded-lg font-semibold hover:bg-blue-700 text-base">Zapisz ELO</button>
            </div>

            <RelationsManager 
              problemId={problem.problem_id}
              title="Powiązane wzory (CKE)"
              initialItems={detailedProblem.formula_matches.map(fm => ({...fm, match_id: fm.matura_formula_match_id}))}
              searchEndpoint="search/formulas/"
              linkEndpoint="matura-formula-matches"
              deleteEndpointPrefix="matura-formula-matches"
              itemKey="matura_formula_id"
              renderItem={renderFormula}
            />

            <RelationsManager 
              problemId={problem.problem_id}
              title="Powiązane zadania maturalne"
              initialItems={detailedProblem.problem_matches.map(pm => ({...pm, match_id: pm.matura_problem_match_id}))}
              searchEndpoint="search/matura-problems/"
              linkEndpoint="matura-problem-matches"
              deleteEndpointPrefix="matura-problem-matches"
              itemKey="matura_problem_id"
              renderItem={renderProblem}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProblemManagerModal;